"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useId, useRef, useState } from "react";
import { DialogHead } from "../internal/DialogHead.js";
import { IconTrash } from "../internal/icons.js";
import { isBackdropClick, useModalDialog } from "../internal/useDialog.js";
import { useEscapeLayer } from "../internal/layers.js";
import { Button } from "./Button.js";
import { TextArea, TextField } from "./TextField.js";
/** Why a field cannot be sent, or "" — `required` and `minlength` read from the element, as the browser would. */
function invalid(element) {
    const value = element.type === "password" ? element.value : element.value.trim();
    if (element.required && !value)
        return "Заполните поле";
    const min = Number(element.getAttribute("minlength") ?? 0);
    if (min > 0 && value.length > 0 && value.length < min)
        return `Не короче ${min} символов`;
    return "";
}
/**
 * Question before a destructive or important action: «×» at the top right, Cancel left of Confirm (Jakob), Esc and a click
 * outside cancel. Enter in a one-line field of the window confirms. Fields of the window (`field` or the host's own inputs
 * with `required` / `minLength`) are checked before `onConfirm`.
 */
export function ConfirmDialog({ open, title, children, icon, tone = "danger", confirmLabel = "Удалить", cancelLabel = "Отмена", closeLabel = "Закрыть", confirmText, confirmTextLabel, confirmTextIgnoreCase, field, onConfirm, onCancel }) {
    const ref = useModalDialog(open);
    const body = useRef(null);
    const titleId = useId();
    const [typed, setTyped] = useState("");
    const [value, setValue] = useState(field?.defaultValue ?? "");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [fieldError, setFieldError] = useState("");
    const [wasOpen, setWasOpen] = useState(open);
    if (open !== wasOpen) {
        setWasOpen(open);
        if (open) {
            setTyped("");
            setValue(field?.defaultValue ?? "");
            setError("");
            setFieldError("");
            setBusy(false);
        }
    }
    const same = (a, b) => confirmTextIgnoreCase ? a.toLocaleLowerCase() === b.toLocaleLowerCase() : a === b;
    const ready = !confirmText || same(typed.trim(), confirmText);
    const cancel = () => { if (!busy)
        onCancel(); };
    useEscapeLayer(open, cancel);
    async function confirm() {
        if (busy || !ready)
            return;
        const fields = [...(body.current?.querySelectorAll("input[required], input[minlength], textarea[required], textarea[minlength]") ?? [])];
        for (const element of fields) {
            const why = invalid(element);
            if (!why)
                continue;
            if (field && element.dataset.confirmField !== undefined)
                setFieldError(why);
            else {
                setError(why);
                element.setAttribute("aria-invalid", "true");
                element.addEventListener("input", () => element.removeAttribute("aria-invalid"), { once: true });
            }
            element.focus();
            return;
        }
        setBusy(true);
        setError("");
        setFieldError("");
        try {
            await onConfirm(field ? (field.type === "password" ? value : value.trim()) : "");
        }
        catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause));
        }
        finally {
            setBusy(false);
        }
    }
    const enter = (event) => {
        const target = event.target;
        if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing || target.tagName !== "INPUT")
            return;
        const type = target.type;
        if (type === "checkbox" || type === "radio" || type === "button" || type === "submit")
            return;
        event.preventDefault();
        void confirm();
    };
    const fieldProps = field && {
        label: field.label, value, type: field.multiline ? undefined : field.type, required: field.required, minLength: field.minLength, maxLength: field.maxLength, placeholder: field.placeholder,
        error: fieldError || undefined, "data-confirm-field": "", autoFocus: !confirmText,
        onChange: (event) => { setValue(event.target.value); setFieldError(""); },
    };
    return _jsx("dialog", { ref: ref, className: "jui-dialog", "data-tone": tone, "aria-labelledby": titleId, role: "alertdialog", onCancel: event => { event.preventDefault(); cancel(); }, onClick: event => { if (isBackdropClick(event))
            cancel(); }, children: open && _jsxs(_Fragment, { children: [_jsx(DialogHead, { titleId: titleId, icon: icon ?? (tone === "danger" ? _jsx(IconTrash, {}) : null), title: title, closeLabel: closeLabel, onClose: cancel, disabled: busy }), _jsxs("div", { ref: body, className: "jui-dialog-body", onKeyDown: enter, children: [children, fieldProps && _jsx("div", { className: "jui-dialog-field", children: field.multiline ? _jsx(TextArea, { ...fieldProps, rows: 3 }) : _jsx(TextField, { ...fieldProps }) }), confirmText && _jsx("div", { className: "jui-dialog-field", children: _jsx(TextField, { label: confirmTextLabel ?? `Введите «${confirmText}» для подтверждения`, value: typed, autoFocus: true, onChange: event => setTyped(event.target.value) }) }), error && _jsx("p", { role: "alert", className: "jui-dialog-error", children: error })] }), _jsxs("footer", { className: "jui-dialog-foot", children: [_jsx(Button, { variant: "quiet", onClick: cancel, disabled: busy, children: cancelLabel }), _jsx(Button, { variant: tone === "danger" ? "danger" : "primary", busy: busy, disabled: !ready, onClick: () => void confirm(), autoFocus: !confirmText && !field, children: confirmLabel })] })] }) });
}
